/**
 * ALLIN Header Absolute JavaScript
 *
 * Features:
 * - Smooth scroll-based logo morphing
 * - 2-row to 1-row transition on scroll
 * - Menu toggles and search functionality
 * - Mobile drawer integration
 */

(function() {
  'use strict';

  class ALLINHeaderAbsolute {
    constructor(header) {
      this.header = header;
      this.isSticky = header.dataset.sticky === 'true';
      this.isTransparentOnHome = header.dataset.transparentOnHome === 'true';

      // Scroll thresholds
      this.scrollThreshold = 100; // When to start transition
      this.scrollComplete = 200;  // When transition is complete

      this.lastScrollY = 0;
      this.isScrolled = false;
      this.ticking = false;

      this.init();
    }

    init() {
      this.setupScrollHandler();
      this.setupSearch();
      this.setupDropdowns();
      this.checkInitialScroll();

      // Add loaded class for entry animations
      requestAnimationFrame(() => {
        this.header.classList.add('allin-header-absolute--loaded');
      });
    }

    setupScrollHandler() {
      if (!this.isSticky && !this.isTransparentOnHome) return;

      // Use passive listener for better performance
      window.addEventListener('scroll', () => {
        if (!this.ticking) {
          window.requestAnimationFrame(() => {
            this.updateHeaderOnScroll();
            this.ticking = false;
          });
          this.ticking = true;
        }
      }, { passive: true });
    }

    updateHeaderOnScroll() {
      const currentScrollY = window.pageYOffset;

      // Calculate scroll progress (0 to 1)
      const progress = Math.min(Math.max((currentScrollY - this.scrollThreshold) / (this.scrollComplete - this.scrollThreshold), 0), 1);

      // Set CSS custom property for smooth interpolation
      this.header.style.setProperty('--scroll-progress', progress);

      // Toggle scrolled class
      if (currentScrollY > this.scrollThreshold) {
        if (!this.isScrolled) {
          this.header.classList.add('allin-header-absolute--scrolled');
          this.isScrolled = true;
        }
      } else {
        if (this.isScrolled) {
          this.header.classList.remove('allin-header-absolute--scrolled');
          this.isScrolled = false;
        }
      }

      // No hide-on-scroll-down behavior - keep header always visible
      // This provides a better UX for e-commerce sites

      this.lastScrollY = currentScrollY;
    }

    checkInitialScroll() {
      // Check if page is already scrolled on load
      if (window.pageYOffset > this.scrollThreshold) {
        this.header.classList.add('allin-header-absolute--scrolled');
        this.isScrolled = true;
        this.header.style.setProperty('--scroll-progress', 1);
      }
    }

    setupSearch() {
      const searchToggle = this.header.querySelector('[data-search-toggle]');
      const searchOverlay = this.header.querySelector('[data-search-overlay]');
      const searchClose = searchOverlay?.querySelector('[data-search-close]');
      const searchInput = searchOverlay?.querySelector('[data-search-input]');
      const searchResults = searchOverlay?.querySelector('[data-search-results]');
      const searchClear = searchOverlay?.querySelector('[data-search-clear]');
      const searchLoading = searchOverlay?.querySelector('[data-search-loading]');
      const searchEmpty = searchOverlay?.querySelector('[data-search-empty]');
      const searchNoResults = searchOverlay?.querySelector('[data-search-no-results]');
      const searchNoResultsTerm = searchOverlay?.querySelector('[data-search-no-results-term]');

      if (!searchToggle || !searchOverlay) return;

      let abortController = null;
      let debounceTimer = null;
      const cachedResults = {};

      // State management — show one of: empty | loading | results | no-results
      const setState = (state, payload) => {
        if (searchEmpty) searchEmpty.hidden = state !== 'empty';
        if (searchResults) searchResults.hidden = state !== 'results';
        if (searchNoResults) searchNoResults.hidden = state !== 'no-results';
        if (searchLoading) searchLoading.hidden = state !== 'loading';

        searchOverlay.classList.toggle('allin-header-absolute__search-overlay--has-content', state !== 'empty');
        searchOverlay.dataset.searchState = state;

        if (state === 'no-results' && searchNoResultsTerm && payload) {
          searchNoResultsTerm.textContent = payload;
        }
        if (state === 'results' && payload && searchResults) {
          searchResults.innerHTML = payload;
        }
      };

      const toggleSearch = (show) => {
        if (show) {
          searchOverlay.classList.add('allin-header-absolute__search-overlay--active');
          document.body.style.overflow = 'hidden';
          setState('empty');
          setTimeout(() => searchInput?.focus(), 150);
        } else {
          searchOverlay.classList.remove('allin-header-absolute__search-overlay--active');
          document.body.style.overflow = '';
          if (searchInput) searchInput.value = '';
          if (searchClear) searchClear.hidden = true;
          setState('empty');
        }
      };

      const fetchResults = (term) => {
        if (abortController) abortController.abort();
        abortController = new AbortController();

        const queryKey = term.replace(/\s+/g, '-').toLowerCase();
        if (cachedResults[queryKey]) {
          if (cachedResults[queryKey].hasResults) {
            setState('results', cachedResults[queryKey].html);
          } else {
            setState('no-results', term);
          }
          return;
        }

        setState('loading');

        const predictiveSearchUrl = window.routes?.predictive_search_url || '/search/suggest';
        fetch(
          `${predictiveSearchUrl}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,query&resources[limit]=6`,
          { signal: abortController.signal }
        )
          .then(response => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then(text => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/html');
            const resultsMarkup = doc.querySelector('#predictive-search-results');

            // Detect if there are actual results (not just the "search for X" footer)
            const productItems = resultsMarkup?.querySelectorAll('.predictive-search__list-item');
            const hasResults = resultsMarkup && productItems && productItems.length > 0;

            if (hasResults) {
              // Strip loading spinners from response
              resultsMarkup.querySelectorAll('.predictive-search__loading-state, .spinner, .loading-overlay__spinner').forEach(el => el.remove());
              const html = resultsMarkup.outerHTML;
              cachedResults[queryKey] = { hasResults: true, html };
              setState('results', html);
            } else {
              cachedResults[queryKey] = { hasResults: false };
              setState('no-results', term);
            }
          })
          .catch(e => {
            if (e.name !== 'AbortError') {
              setState('no-results', term);
            }
          });
      };

      // Listen for input
      searchInput?.addEventListener('input', () => {
        const term = searchInput.value.trim();
        clearTimeout(debounceTimer);

        // Toggle clear button visibility
        if (searchClear) searchClear.hidden = term.length === 0;

        if (term.length === 0) {
          setState('empty');
          return;
        }
        if (term.length < 2) {
          // Don't fetch yet, but don't show empty either — show loading hint
          setState('loading');
          return;
        }
        debounceTimer = setTimeout(() => fetchResults(term), 250);
      });

      // Clear button
      searchClear?.addEventListener('click', () => {
        if (!searchInput) return;
        searchInput.value = '';
        searchInput.focus();
        if (searchClear) searchClear.hidden = true;
        setState('empty');
      });

      searchToggle.addEventListener('click', () => toggleSearch(true));
      searchClose?.addEventListener('click', () => toggleSearch(false));

      // Close on click outside (but not on suggestion clicks)
      searchOverlay?.addEventListener('click', (e) => {
        if (e.target === searchOverlay) {
          toggleSearch(false);
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && searchOverlay.classList.contains('allin-header-absolute__search-overlay--active')) {
          toggleSearch(false);
        }
      });

      // Close overlay when user clicks a result link (so they navigate cleanly)
      searchOverlay?.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (link && searchOverlay.contains(link)) {
          // Let the navigation happen, then close after small delay
          setTimeout(() => toggleSearch(false), 100);
          return;
        }

        // Handle "Suche nach X" footer button — navigate to /search?q=term
        const searchForButton = e.target.closest('#search-for-button, .predictive-search-option-search-keywords');
        if (searchForButton && searchOverlay.contains(searchForButton)) {
          e.preventDefault();
          const term = searchInput?.value.trim();
          if (term) {
            const searchUrl = (window.routes?.search_url || '/search') + '?q=' + encodeURIComponent(term) + '&type=product';
            toggleSearch(false);
            window.location.href = searchUrl;
          }
        }
      });
    }

    setupDropdowns() {
      const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      const dropdownItems = this.header.querySelectorAll('.allin-header-absolute__nav-item--has-dropdown');
      const megaMenuItems = this.header.querySelectorAll('.allin-header-absolute__nav-item.submenu--megamenu');
      const allNavItems = [...dropdownItems, ...megaMenuItems];

      if (isTouchDevice) {
        // Touch: tap to open, second tap to navigate
        allNavItems.forEach(item => {
          const link = item.querySelector('.allin-header-absolute__nav-link');
          link?.addEventListener('click', (e) => {
            const isActive = item.classList.contains('megamenu--active') ||
                             item.classList.contains('allin-header-absolute__nav-item--active');
            if (!isActive) {
              e.preventDefault();
              this.closeAllMenus(allNavItems);
              const cls = item.classList.contains('submenu--megamenu') ? 'megamenu--active' : 'allin-header-absolute__nav-item--active';
              item.classList.add(cls);
            }
          });
        });

        document.addEventListener('click', (e) => {
          if (!e.target.closest('.allin-header-absolute__nav-item')) {
            this.closeAllMenus(allNavItems);
          }
        });
      } else {
        // Desktop: hover intent with open/close delays
        this.setupHoverIntent(dropdownItems, 'allin-header-absolute__nav-item--active', allNavItems);
        this.setupHoverIntent(megaMenuItems, 'megamenu--active', allNavItems);
      }
    }

    closeAllMenus(items) {
      items.forEach(item => {
        item.classList.remove('megamenu--active', 'allin-header-absolute__nav-item--active');
      });
    }

    setupHoverIntent(items, activeClass, allNavItems) {
      const OPEN_DELAY = 120;   // ms before opening
      const CLOSE_DELAY = 280;  // ms before closing (time to move mouse to panel)

      items.forEach(item => {
        let openTimer = null;
        let closeTimer = null;

        const open = () => {
          clearTimeout(closeTimer);
          closeTimer = null;
          if (item.classList.contains(activeClass)) return;
          openTimer = setTimeout(() => {
            // Close others first
            allNavItems.forEach(other => {
              if (other !== item) {
                other.classList.remove('megamenu--active', 'allin-header-absolute__nav-item--active');
              }
            });
            item.classList.add(activeClass);
          }, OPEN_DELAY);
        };

        const close = () => {
          clearTimeout(openTimer);
          openTimer = null;
          closeTimer = setTimeout(() => {
            item.classList.remove(activeClass);
          }, CLOSE_DELAY);
        };

        const cancelClose = () => {
          clearTimeout(closeTimer);
          closeTimer = null;
        };

        // Nav link hover
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);

        // Mega menu panel hover — cancel close when mouse enters panel
        const panel = item.querySelector('.megamenu-container') || item.querySelector('.allin-header-absolute__dropdown');
        if (panel) {
          panel.addEventListener('mouseenter', cancelClose);
          panel.addEventListener('mouseleave', close);
        }
      });
    }
  }

  // ALLINHeaderDrawer component for mobile menu
  class ALLINHeaderDrawer extends HTMLElement {
    constructor() {
      super();
      this.drawer = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.menuDrawer = this.querySelector('.menu-drawer');
      this.scrollPosition = 0;
    }

    connectedCallback() {
      if (this.summary) {
        this.summary.addEventListener('click', this.onSummaryClick.bind(this));
      }

      // Find and attach close button handlers
      this.closeButtons = this.querySelectorAll('.close-menu-js, .menu-drawer__close-button');
      this.closeButtons.forEach(button => {
        button.addEventListener('click', this.onCloseButtonClick.bind(this));
      });

      // Setup submenu handlers
      this.setupSubmenus();

      // Overlay click to close
      this.overlay = this.querySelector('.drawer__outer-background');
      if (this.overlay) {
        this.overlay.addEventListener('click', this.onOverlayClick.bind(this));
      }

      // Close on escape key
      document.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape' && this.drawer?.hasAttribute('open')) {
          this.close();
        }
      });

      // Check breakpoint
      const breakpoint = this.dataset.breakpoint;
      if (breakpoint) {
        this.checkBreakpoint(breakpoint);
        window.addEventListener('resize', () => this.checkBreakpoint(breakpoint));
      }
    }

    onSummaryClick(event) {
      if (event.target !== this.summary && !this.summary.contains(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (this.drawer.hasAttribute('open')) {
        this.close();
      } else {
        this.open();
      }
    }

    onCloseButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }

    onOverlayClick(event) {
      event.preventDefault();
      this.close();
    }

    open() {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      this.drawer.setAttribute('open', '');
      this.summary?.setAttribute('aria-expanded', 'true');

      // Prevent background scrolling
      document.documentElement.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.classList.add('overflow-hidden');

      // Focus management
      if (this.menuDrawer) {
        const focusElement = this.menuDrawer.querySelector('.menu-drawer__close-button');
        if (focusElement) {
          setTimeout(() => {
            focusElement.focus();
            trapFocus(this.menuDrawer, focusElement);
          }, 100);
        }
      }
    }

    close() {
      this.drawer.removeAttribute('open');
      this.summary?.setAttribute('aria-expanded', 'false');

      // Close all submenus
      const openSubmenus = this.querySelectorAll('details.menu-drawer__submenu[open]');
      openSubmenus.forEach(submenu => submenu.removeAttribute('open'));

      // Restore body scrolling
      document.documentElement.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('overflow-hidden');

      // Restore scroll position
      window.scrollTo(0, this.scrollPosition);

      removeTrapFocus(this.summary);
    }

    setupSubmenus() {
      const submenuSummaries = this.querySelectorAll('.menu-drawer__submenu > summary');

      submenuSummaries.forEach(summary => {
        summary.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();

          const details = summary.parentElement;
          const isOpen = details.hasAttribute('open');

          if (!isOpen) {
            details.setAttribute('open', '');
            summary.setAttribute('aria-expanded', 'true');
          } else {
            details.removeAttribute('open');
            summary.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }

    checkBreakpoint(breakpoint) {
      if (breakpoint === 'desktop' && window.innerWidth > 992) {
        this.style.display = 'none';
      } else {
        this.style.display = 'block';
      }
    }
  }

  // Focus trap helpers
  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  }

  let trapFocusHandlers = {};

  function trapFocus(container, elementToFocus = container) {
    if (!container) return;
    const elements = getFocusableElements(container);
    const first = elements[0];
    const last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== last && event.target !== first) return;
      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function () {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function (event) {
      if (event.code !== 'Tab') return;

      if (event.target === last && !event.shiftKey && first) {
        event.preventDefault();
        first.focus();
      }

      if ((event.target === container || event.target === first) && event.shiftKey && last) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      elementToFocus.focus();
    }
  }

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  }

  // Initialize on DOM ready
  function initHeaders() {
    // Initialize ALLIN headers
    const headers = document.querySelectorAll('.allin-header-absolute');
    headers.forEach(header => new ALLINHeaderAbsolute(header));

    // Define custom elements
    if (!customElements.get('allin-header-drawer')) {
      customElements.define('allin-header-drawer', ALLINHeaderDrawer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaders);
  } else {
    initHeaders();
  }

  // Expose to global scope
  window.ALLINHeaderAbsolute = ALLINHeaderAbsolute;
  window.trapFocus = trapFocus;
  window.removeTrapFocus = removeTrapFocus;
})();
